import {cloneDeep} from 'lodash';
import {List} from 'immutable';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {ExpressionPath, Expressions, Plugins} from '@packages/systems/wfdl';
import {getDynBindingsFromElement} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {ASSET_PATH, CHILDREN_PATH} from '@packages/utilities/platform';
import {
  isDivBlock,
  isImageElement,
  isLink,
  isTextHolder,
  isVideoElement,
} from '@packages/utilities/elementTypeUtils';
import {DEFAULT_SKU_SLUG} from '@packages/systems/commerce/constants';
import {getDynamoBindingPaths} from '@packages/systems/dynamo/wfdl';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {ImageRefRecord} from '@packages/systems/core/records/ImageRefRecord';
import {isFileUploading} from '@packages/shared/utils/AssetUtils';
import {ReadableState, UndoableState} from '@packages/systems/undoRedo';
import type {JSONNode} from '@packages/shared/types/JSONNode';
import {compressJSONNodes} from '@packages/systems/tree';

type LegacyBindingProperty = 'innerHTML' | 'src' | 'href';

type TargetBindingsType = {
  universal: ExpressionPath | null;
  legacy?: Array<LegacyBindingProperty>;
};

type AllowedElementTargetsType = {
  checkFn: (element: ExpressionElement) => boolean;
  bindings: TargetBindingsType;
};

/**
 * Configuration of allowed element targets and their bindings.
 *
 * Legacy binding contexts are user systems, e-commerce and site search.
 *
 * Legacy elements are Links (https://webflow.slack.com/archives/C02UF2LLM0X/p1706883710846769)
 */
const allowedElementTargets: Array<AllowedElementTargetsType> = [
  {
    // must be before text holder as we prioritize goto the non-visual binding
    checkFn: (element: ExpressionElement): boolean => isLink(element),
    bindings: {
      // links do not support universal bindings
      universal: null,
      // prioritize finding the bound link then the bound text
      legacy: ['href', 'innerHTML'],
    },
  },
  {
    checkFn: (element: ExpressionElement): boolean =>
      isTextHolder(element) || isDivBlock(element),
    bindings: {
      universal: CHILDREN_PATH,
      legacy: ['innerHTML'],
    },
  },
  {
    checkFn: (element: ExpressionElement): boolean => isImageElement(element),
    bindings: {
      universal: ASSET_PATH,
      legacy: ['src'],
    },
  },
  {
    checkFn: (element: ExpressionElement): boolean => isVideoElement(element),
    bindings: {
      universal: [{in: 'Record', at: 'video'}],
      legacy: ['innerHTML'],
    },
  },
];

const getLegacyBindingSlug = (
  element: ExpressionElement,
  legacyBindingProperty: LegacyBindingProperty
): string | null => {
  const dynBindingsMap = getDynBindingsFromElement(element);
  const dynamicBinding = dynBindingsMap[legacyBindingProperty];

  if (Array.isArray(dynamicBinding) && dynamicBinding.length) {
    const [binding] = dynamicBinding;

    if (!binding || !binding.id || !binding.slug) return null;

    // edge case for e-commerce where the first binding may be the default variant
    if (binding.slug === DEFAULT_SKU_SLUG) {
      const [_, nextBinding] = dynamicBinding;

      return nextBinding && nextBinding.id ? nextBinding.slug || null : null;
    }

    return binding.slug;
  }

  return null;
};

const getUniversalBindingSlug = (
  element: ExpressionElement,
  bindingPath: ExpressionPath
): string => {
  let maybeBindingSlug = null;

  const elementData = Expressions.getElementData(element);
  const elementBindingData = Expressions.getIn(elementData, bindingPath);

  const universalBindingsBindingPath = elementBindingData
    ? getDynamoBindingPaths(elementBindingData)
    : null;

  if (universalBindingsBindingPath && universalBindingsBindingPath.length) {
    maybeBindingSlug =
      universalBindingsBindingPath[0] && universalBindingsBindingPath[0][0];
  }

  return maybeBindingSlug || '';
};

const safelyGetBindingSlug = (
  element: ExpressionElement,
  bindingPath: ExpressionPath | null,
  legacyBindingProperty?: Array<LegacyBindingProperty>
): string | null => {
  if (legacyBindingProperty) {
    for (const property of legacyBindingProperty) {
      const maybeLegacyBindingSlug = getLegacyBindingSlug(element, property);

      if (maybeLegacyBindingSlug) return maybeLegacyBindingSlug;
    }
  }

  if (!bindingPath) return null;

  return getUniversalBindingSlug(element, bindingPath) ?? null;
};

/**
 * Safely get the slug of the field the provided element is bound to, if it is bound.
 * This function supports both universal and legacy bindings.
 *
 * @param element the target element
 * @returns a string field slug if one exists for the element, null otherwise
 */
export const getBoundTargetFieldSlug = (
  element: ExpressionElement
): string | null => {
  for (const target of allowedElementTargets) {
    if (target.checkFn(element)) {
      return safelyGetBindingSlug(
        element,
        target.bindings.universal,
        target.bindings.legacy
      );
    }
  }

  return null;
};

/**
 * Preserves metadata fields and autosave data from the current state
 * alongside content from the previous state.
 * Used for undo redo operations to ensure certain data isn't overwritten.
 *
 * @param prevStore the previous state
 * @param currentStore the current state
 * @returns a new state with preserved fields (currently only item metadata and autosave data)
 */
export const getCMSStateWithPreservedFields = (
  prevStore: ReadableState['CurrentCollectionStore'],
  currentStore: UndoableState['CurrentCollectionStore']
): ReadableState['CurrentCollectionStore'] => {
  if (!prevStore?.currentItem || !currentStore?.currentItem) {
    return prevStore;
  }

  const fieldsToPreserve = [
    '_archived',
    '_draft',
    'published-by',
    'published-on',
    '_translating',
    'updated-by',
    'updated-on',
  ];

  // Create a new item with content from old state while preserving metadata from current state
  let currentItemWithPreservedFields = prevStore.currentItem;
  fieldsToPreserve.forEach((field) => {
    if (currentStore?.currentItem?.has(field)) {
      currentItemWithPreservedFields = currentItemWithPreservedFields.set(
        field,
        currentStore?.currentItem?.get(field)
      );
    }
  });

  // Preserve additional values from current state
  return prevStore
    .set('currentItem', currentItemWithPreservedFields)
    .set('currentInlineItem', currentStore.currentInlineItem)
    .set('autosaveData', currentStore.autosaveData)
    .set('showInDataManagerModal', currentStore.showInDataManagerModal)
    .set('translationsLoading', currentStore.translationsLoading);
};

/**
 * Handles CMS image upload state management for undo redo operations in order to prevent undo redo actions
 * from restoring an intermediate uploading state rather than the actual pre and post upload state.
 * - When upload starts: stores the pre upload state and skips recording subsequent intermediary actions
 * - During upload: continues skipping action recording to avoid intermediary states
 * - When upload completes: uses the stored pre upload state for undo instead of the immediate previous state
 *
 * @param prevState - the previous state
 * @param nextState - the next state after the action
 * @returns an object containing:
 *   - shouldSkipRecorder: true if this action should not be added to undo redo stack
 *   - cmsPrevState: the state to use for undo operations (either prevState or the stored pre upload state)
 */
// Variable to store the pre upload state for image uploads - this is declared outside of the function to persist
// across multiple calls and track upload state correctly
let cmsPreImageUploadState: ReadableState | null = null;
export const getCMSUndoState = (
  prevState: ReadableState,
  nextState: UndoableState
): {
  shouldSkipRecorder: boolean;
  cmsPrevState: ReadableState;
} => {
  const nextItem = nextState.CurrentCollectionStore?.currentItem;
  const prevItem = prevState.CurrentCollectionStore?.currentItem;
  const hasUploads = nextItem && hasUploadingImages(nextItem);
  const prevHasUploads = prevItem && hasUploadingImages(prevItem);

  if (hasUploads) {
    // Image upload started: store the pre upload state
    if (!prevHasUploads) {
      cmsPreImageUploadState = prevState;
    }
    // Image upload in progress: set shouldSkipRecorder to true to skip adding this action to the stack
    return {shouldSkipRecorder: true, cmsPrevState: prevState};
  }

  // Image upload complete: clear the pre upload state and return the pre upload state
  if (!hasUploads && prevHasUploads && cmsPreImageUploadState) {
    const preUploadState = cmsPreImageUploadState;
    cmsPreImageUploadState = null;
    return {shouldSkipRecorder: false, cmsPrevState: preUploadState};
  }

  // No uploads or special handling required: return the previous state
  return {shouldSkipRecorder: false, cmsPrevState: prevState};
};

/**
 * Iterate over the item and check if any of the fields are Lists and have uploading images
 * @param item the item to check
 * @returns true if the item has any uploading images, false otherwise
 */
export const hasUploadingImages = (item: ItemRecordType) =>
  item?.some((field) => {
    return List.isList(field)
      ? (field as List<ImageRefRecord | null | undefined>).some(isFileUploading)
      : isFileUploading(field);
  }) ?? false;

/**
 * Normalizes rich text JSON nodes by compressing them to remove default values.
 * This ensures consistent comparison between client-generated values (which are already
 * compressed via getElementAsJSONNodesCompressed) and server values (which contain
 * explicit defaults).
 *
 * The compressJSONNodes function only mutates node.data, so we shallow clone nodes
 * and deep clone only the data property for better performance with large RTF content.
 *
 * After compression, we also remove empty data objects to match nodes that never had data.
 * This matches the pattern in ExportElementUtils.ts where json.data is only set if it has keys.
 */
export const normalizeRichTextNodes = (
  nodes: JSONNode[],
  plugins: Plugins.PluginsType
): JSONNode[] => {
  // Only deep clone node.data since that's what compressJSONNodes mutates.
  // This avoids expensive deep cloning of children, classes, and other large arrays.
  const clonedNodes = nodes.map((node) =>
    node.data ? {...node, data: cloneDeep(node.data)} : node
  );
  compressJSONNodes(plugins)(clonedNodes);

  // Remove empty data objects to match nodes that never had data.
  // This ensures {data: {}} is equivalent to no data field at all.
  // We create new objects instead of using delete for better performance.
  return removeEmptyDataObjects(clonedNodes);
};

/**
 * Removes empty data objects from the provided JSON nodes.
 *
 * Returns a shallow clones of the input nodes with empty data objects removed.
 * This function is used to normalize rich text JSON nodes after compression and
 * can be used to normalize compressed JSON nodes before comparison with
 * normalized rich text nodes.
 */
export function removeEmptyDataObjects(nodes: JSONNode[]): JSONNode[] {
  return nodes.map((node) => {
    if (node.data && Object.keys(node.data).length === 0) {
      const {data: _data, ...nodeWithoutData} = node;
      return nodeWithoutData as JSONNode;
    }
    return node;
  });
}
